<script lang="ts" setup generic="T extends string | Component">
import {
  computed,
  useCssModule,
  useSlots,
  h,
  mergeProps,
  useAttrs,
  type Component,
  type VNode,
} from 'vue';
import { IconClose } from '@/icons';
import type { PolymorphicProps } from '../../shared/types';
interface Props extends PolymorphicProps<T> {
  filled?: boolean;
  removable?: boolean;
  active?: boolean;
  variant?: 'default' | 'action-panel' | 'action-panel-neutral';
}
defineOptions({
  inheritAttrs: false,
});
const props = defineProps<Props>();
const emit = defineEmits<{
  remove: [];
}>();
defineSlots<{
  default?: () => VNode;
  meta?: () => VNode;
  leadingVisual?: () => VNode;
  counter?: () => VNode;
}>();
const slots = useSlots();
const attrs = useAttrs();
const style = useCssModule();
const rootProps = computed(() => {
  return {
    class: [
      style.root,
      {
        [style.filled]: props.filled,
        [style.removable]: props.removable,
        [style.active]: props.active,
        [style.actionPanel]: props.variant === 'action-panel',
        [style.actionPanelNeutral]: props.variant === 'action-panel-neutral',
      },
    ],
    'data-ui-component': !props.removable ? 'UiChip' : undefined,
  };
});
defineRender(() => {
  const text = slots.default?.();
  const meta = slots.meta?.();
  const counter = slots.counter?.();
  const leadingVisual = slots.leadingVisual?.();
  const internalStructure = [
    h('span', { class: style.content }, [
      leadingVisual ? h('span', { class: style.leadingVisual }, leadingVisual) : null,
      text ? h('span', { class: style.text }, text) : null,
    ]),
    counter ? h('span', { class: style.counter }, counter) : null,
    meta ? h('span', { class: style.meta }, meta) : null,
  ];
  const content = props.as
    ? h(props.as, mergeProps(attrs, rootProps.value, props.asProps ?? {}), internalStructure)
    : h('span', rootProps.value, internalStructure);
  return props.removable
    ? h(
        'span',
        {
          class: [
            style.wrapper,
            {
              [style.actionPanelWrapper]: props.variant === 'action-panel',
              [style.actionPanelWrapperNeutral]: props.variant === 'action-panel-neutral',
              [style.actionPanelWrapperActive]:
                (props.variant === 'action-panel' || props.variant === 'action-panel-neutral') &&
                props.active,
            },
          ],
          'data-ui-component': 'UiChip',
        },
        [
          content,
          h(
            'button',
            {
              type: 'button',
              class: [
                style.remove,
                {
                  [style.actionPanelRemove]: props.variant === 'action-panel',
                  [style.actionPanelRemoveNeutral]: props.variant === 'action-panel-neutral',
                },
              ],
              onclick: (event: MouseEvent) => {
                event.preventDefault();
                event.stopPropagation();
                emit('remove');
              },
            },
            h(IconClose, { ariaHidden: true }),
          ),
        ],
      )
    : content;
});
</script>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/iconography';
@use '../../styles/shared';
@use '../../styles/typography';

.wrapper {
  display: inline-flex;
  position: relative;

  &:hover {
    .remove {
      opacity: 1;
    }
  }
}

.wrapper:not(.actionPanelWrapper):hover {
  a.root,
  button.root {
    background: rgba(0, 0, 0, 0.04);
  }
}

.actionPanelWrapper {
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(34, 107, 108, 0.12);
  transition: background-color 120ms ease;
}

.actionPanelWrapperNeutral {
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
  background: var(--black-shade-8, rgba(0, 0, 0, 0.08));
  transition: background-color 120ms ease;
}

.actionPanelWrapperActive,
.actionPanelWrapperActive:hover,
.actionPanelWrapperActive:focus-within {
  background:
    linear-gradient(0deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%),
    rgba(34, 107, 108, 0.12);
}

.root {
  position: relative;
  font-family: typography.$font-family-primary;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  box-sizing: border-box;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  height: 24px;
  border-radius: 16px;
  border: 1px solid #d5d6d9;
  background: transparent;
  color: #010000;
  padding: 0 (8px - 1px);

  &.active {
    background: rgba(0, 0, 0, 0.08);
    color: #226b6c;
  }

  &.filled {
    color: #226b6c;
    background: rgba(34, 107, 108, 0.12);
    border: none;
    padding: 0 8px;

    &.active {
      background:
        linear-gradient(0deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%),
        rgba(34, 107, 108, 0.12);
    }
  }

  &.actionPanel {
    height: 28px;
    border-radius: 6px;
    border: none;
    padding: 0 8px;
    background: rgba(34, 107, 108, 0.12);
    color: #226b6c;
    gap: 6px;

    &.removable {
      padding-right: 4px;
      background: transparent;
    }
  }

  &.actionPanelNeutral {
    height: 28px;
    border-radius: 6px;
    border: none;
    padding: 0 8px;
    background: var(--black-shade-8, rgba(0, 0, 0, 0.08));
    color: #010000;
    gap: 6px;

    &.removable {
      padding-right: 4px;
      background: transparent;
    }
  }
}

a.root,
button.root {
  cursor: pointer;

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
  }

  .wrapper:hover &,
  &:hover {
    background: rgba(0, 0, 0, 0.04);

    &.filled {
      background:
        linear-gradient(0deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.04) 100%),
        rgba(34, 107, 108, 0.12);
    }
  }

  &:active {
    background: rgba(0, 0, 0, 0.08);
    color: #226b6c;

    &.filled {
      background:
        linear-gradient(0deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%),
        rgba(34, 107, 108, 0.12);
    }
  }
}

a.root.actionPanel.removable,
button.root.actionPanel.removable {
  .actionPanelWrapper:hover > &,
  .actionPanelWrapper:focus-within > & {
    background: transparent;
  }

  .actionPanelWrapper > &:hover,
  .actionPanelWrapper > &:focus-visible {
    background:
      linear-gradient(0deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.04) 100%),
      rgba(34, 107, 108, 0.12);
  }

  .actionPanelWrapper > &:active {
    background:
      linear-gradient(0deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%),
      rgba(34, 107, 108, 0.12);
  }
}

a.root.actionPanelNeutral.removable,
button.root.actionPanelNeutral.removable {
  .actionPanelWrapperNeutral:hover > &,
  .actionPanelWrapperNeutral:focus-within > & {
    background: transparent;
  }

  .actionPanelWrapperNeutral > &:hover,
  .actionPanelWrapperNeutral > &:focus-visible {
    background:
      linear-gradient(0deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.04) 100%),
      var(--black-shade-8, rgba(0, 0, 0, 0.08));
  }

  .actionPanelWrapperNeutral > &:active {
    background:
      linear-gradient(0deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%),
      var(--black-shade-8, rgba(0, 0, 0, 0.08));
    color: #010000;
  }
}

.content {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.actionPanel .content {
  gap: 6px;
}

.leadingVisual {
  width: 18px;
  height: 18px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  > img,
  > svg {
    max-width: 100%;
    max-height: 100%;
  }
}

.counter {
  font-family: typography.$font-family-monospace;
  color: #f5f5f5;
  background-color: #226b6c;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 14px;
  padding: 0 4px;
  box-sizing: border-box;
}

.text {
  max-width: 220px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.meta {
  color: #655e60;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  max-width: 120px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.actionPanel .meta {
  max-width: 180px;
}

.remove {
  display: flex;
  width: 18px;
  height: 18px;
  padding: 2px;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  position: absolute;
  z-index: 10;
  top: 50%;
  transform: translateY(-50%);
  right: 2px;
  background-color: #fff;
  color: #010000;
  border-radius: 100%;
  opacity: 0;
  border: none;
  cursor: pointer;

  > svg {
    width: 100%;
    height: 100%;
  }

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
    opacity: 1;
  }
}

.actionPanelRemove {
  position: static;
  transform: none;
  z-index: auto;
  width: 28px;
  height: 28px;
  padding: 5px;
  border-radius: 6px;
  background: transparent;
  color: #010000;
  border: 1px solid transparent;
  opacity: 1;

  &:hover {
    background: rgba(35, 57, 93, 0.05);
  }

  &:active {
    background: rgba(35, 57, 93, 0.1);
    color: #226b6c;
  }

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
  }
}

.actionPanelRemoveNeutral {
  position: static;
  transform: none;
  z-index: auto;
  width: 28px;
  height: 28px;
  padding: 5px;
  border-radius: 6px;
  background: transparent;
  color: #010000;
  border: 1px solid transparent;
  opacity: 1;

  &:hover {
    background: rgba(35, 57, 93, 0.05);
  }

  &:active {
    background: rgba(35, 57, 93, 0.1);
    color: #010000;
  }

  &:focus-visible {
    outline: 1px solid #085cd9;
    outline-offset: -1px;
  }
}
</style>
